<template>
  <v-container v-resize="onResize" class="product__container pa-0" fluid>
    <ContentMenu
      v-if="showContentMenu"
      class="product-content-menu"
      :class="{ hidden: !showContentMenu }"
      :activated-menu="activatedMenu"
      :items="contentMenu"
    />

    <div class="product-info-container d-flex justify-space-between">
      <div class="product-content-container">
        <v-sheet class="product-content__block--album" elevation="0" rounded>
          <client-only>
            <!-- 相片列表 -->
            <album-swiper class="product-slide" :images="product.images" :hide-right-side="isMobile" />
          </client-only>
          <div v-if="!isMdAndUp" class="album-info">
            <p class="branch-name mb-2" @click="goToBranch">{{ product.branch_name }}</p>

            <div class="product-name">{{ product.product_name }}</div>

            <p v-if="product.distance" class="distance-text mt-1 mb-0">
              {{ $t('distance_value', { value: product.distance }) }}
            </p>
          </div>
        </v-sheet>

        <!-- 商品內容 -->
        <v-sheet v-if="isMainProductInfoShown" id="main-product-info" class="product-content__block" elevation="0" rounded>
          <ProductInfoSheet :product="product" :should-reward-hide="isFunBookV2" />
        </v-sheet>

        <!-- 商品介紹 -->
        <v-sheet v-if="product.productIntro?.length > 0" id="main-product-intro" class="product-content__block" elevation="0" rounded>
          <ProductIntro :intro-list="product.productIntro" />
        </v-sheet>

        <!-- 價目表 -->
        <v-sheet v-if="product.price_list?.length > 0" id="main-product-price-list" class="product-content__block" elevation="0" rounded>
          <ProductPriceList :price-list="product.price_list" :symbol-code="product.symbol_code" />
        </v-sheet>

        <!-- 購買須知 -->
        <v-sheet
          v-if="product.productShoppingNotices?.length > 0"
          id="main-product-shopping-notice"
          class="product-content__block"
          elevation="0"
          rounded
        >
          <ProductShoppingNotice :shopping-notices="product.productShoppingNotices" />
        </v-sheet>

        <!-- 評論 -->
        <!-- 之後在 FunBook 會有評論功能，先暫時拿掉，但評論裡的名人推薦一直都不會有 -->
        <!-- 商店評價 -->
        <!-- <v-sheet v-if="comments.total_count > 0" id="main-product-comments" class="product-content__block px-0" elevation="0" rounded>
          <product-comment-sheet
            ref="productCommentSheet"
            :comments="comments"
            :branch="branch"
            :product="product"
            :is-md-and-up="isMdAndUp"
            :shouldHideCelebritiesRecommend="isFunBookV2"
          />
        </v-sheet> -->

        <!-- 店家特色 -->
        <v-sheet id="main-branch-intro" class="product-content__block" elevation="0" rounded>
          <branch-intro-sheet :product="product" :is-mobile="isMobile" />
        </v-sheet>

        <!-- 店家位置 -->
        <v-sheet id="main-branch-position" class="product-content__block" elevation="0" rounded>
          <product-position-sheet :branch="branch" :product="product" />
        </v-sheet>

        <!-- 注意事項 & 客戶須知 -->
        <v-sheet id="main-product-notices" class="product-content__block product-content__block--notice mb-0" elevation="0" rounded>
          <product-notices-sheet :product="product" />
        </v-sheet>
      </div>

      <div v-if="isMdAndUp" class="booking-card__web">
        <BookingProductCard
          :style="[bookingProductCardStyle]"
          :product="product"
          :comments="branchComments"
          :onboard="onboard"
          should-hide-comments
          :is-book-seat-only="product.isBookSeatOnly"
          :is-paying-onsite="product.isPaidOnsite"
          :is-only-one-button-show="product.isBookSeatOnly && !product.isPaidOnsite"
          :show-price="!product.isBookSeatOnly"
          should-hide-rrp
          should-hide-gift-button
        />
      </div>
    </div>

    <!-- 推薦區塊 -->
    <v-sheet v-if="showRecommendSheet" class="recommend-product__container" elevation="0" rounded>
      <div v-if="otherProducts.length > 0" class="sub-content__block">
        <product-swiper :title="$t('merchant_other_product')" :items="otherProducts" :source="GtmSource.PRODUCT_DETAIL_SAME_BRANCH" />
      </div>
    </v-sheet>

    <v-sheet v-if="isMdAndDown" class="booking__footer-banner d-flex align-center px-5 py-2" height="64">
      <BookingBanner
        :is-only-one-button-show="product.isBookSeatOnly && !product.isPaidOnsite"
        :show-price="!product.isBookSeatOnly"
        should-hide-rrp
        :show-rating="false"
        :is-paying-onsite="product.isPaidOnsite"
        :onboard="onboard"
        :rating="product.rating"
        :comments="branchComments"
        :price-setting="{ symbolCode: product.symbol_code, discount: product.discount, rrp: product.rrp, srrp: product.rrp }"
        @open-comment-dialog="openCommentDialog"
      />
    </v-sheet>

    <json-ld-product :product="product" :comments="comments" />
    <json-ld-breadcrumb :all-regions="allRegions" :region="region" :branch="branch" :product="product" />

    <!-- 導下載視窗 -->
    <guide-download-app-modal />
  </v-container>
</template>

<script>
import '@/assets/sass/pages/product-info.scss'
import { mapGetters } from 'vuex'
import { mapState } from 'pinia'
import { useEventListener } from '@vueuse/core'

import tools from '@/mixin/tools'
import notificationComposition from '@/composition/notification'
import { usePlatformProductDetail } from '@/composition/useProductInfo'
import { useReferer } from '@/composition/useReferer'
import { useGoogleAppointment } from '@/composition/useGoogleAppointment'

import utils from '@/utils'
import { ProductSalesMode } from '@/view-models/product'
import { GtmSource } from '@/view-models/gtm'
import { usePlatformStore } from '@/store-pinia/platform'

import ProductIntro from '@/components/page/products/ProductIntro.vue'
import ProductInfoSheet from '@/components/page/products/product-info-sheet.vue'
import ProductPriceList from '@/components/page/products/ProductPriceList.vue'
import ProductShoppingNotice from '@/components/page/products/ProductShoppingNotice.vue'
import BranchIntroSheet from '@/components/page/products/branch-intro-sheet.vue'
import ProductPositionSheet from '@/components/page/products/product-position-sheet.vue'
import ProductNoticesSheet from '@/components/page/products/product-notices-sheet.vue'
import JsonLdProduct from '@/components/jsonld/product.vue'
import JsonLdBreadcrumb from '@/components/jsonld/breadcrumb.vue'
import BookingProductCard from '@/components/card/booking-product-card.vue'
import AlbumSwiper from '@/components/slide/album-swiper.vue'
import { GOOGLE_BUSINESS_VERTICAL, PaymentMode } from '@/constants/ads'

export default defineNuxtComponent({
  name: 'ProductInfo',
  components: {
    ContentMenu: defineAsyncComponent(() => import('@/components/navigation/content-menu.vue')),
    GuideDownloadAppModal: defineAsyncComponent(() => import('@/components/dialog/guide-download-app-modal.vue')),
    BookingBanner: defineAsyncComponent(() => import('@/components/page/products/booking-banner.vue')),
    ProductIntro,
    ProductInfoSheet,
    ProductPriceList,
    ProductShoppingNotice,
    ProductPositionSheet,
    BranchIntroSheet,
    ProductNoticesSheet,
    ProductSwiper: defineAsyncComponent(() => import('@/components/swiper/product-swiper.vue')),
    JsonLdProduct,
    JsonLdBreadcrumb,
    BookingProductCard,
    AlbumSwiper,
  },
  mixins: [tools],
  setup() {
    definePageMeta({
      middleware: ['products-funbook-middleware'],
    })

    const nuxtApp = useNuxtApp()
    const route = useRoute()

    const { getFlashNotification, getDeliveryNotification } = notificationComposition()
    const { isFunBookV2 } = storeToRefs(usePlatformStore())
    const { product } = usePlatformProductDetail()
    const bid = route.params.branch_id

    useReferer({ pid: product.value.pid })
    const { deleteGoogleAppointment } = useGoogleAppointment(bid)

    const { productDetailTitle, productDescriptionTitle } = usePlatformProductContext()

    useHead(() => {
      const pageTitle = `${product.value.product_name} - ${product.value.branch_name} | ${nuxtApp.$i18n.t('default_link_title_funbook')}`
      const ogImage = utils.CDN + utils.imagePath(product.value.cover_image)
      return {
        title: pageTitle,
        meta: [
          { hid: 'title', name: 'title', content: pageTitle },
          { hid: 'description', name: 'description', content: utils.removeNewLine(product.value.description) },
          { hid: 'keywords', name: 'keywords', content: product.value.keyword || '' },
          { hid: 'og:title', property: 'og:title', content: pageTitle },
          { hid: 'og:description', property: 'og:description', content: utils.removeNewLine(product.value.description) },
          { hid: 'og:image', property: 'og:image', content: ogImage },
          { name: 'robots', content: 'noindex' },
        ],
      }
    })

    onMounted(() => {
      useEventListener(window, 'beforeunload', deleteGoogleAppointment)
    })
    const localePath = useLocalePath()

    return {
      getFlashNotification,
      getDeliveryNotification,
      isFunBookV2,
      product,
      productDetailTitle,
      productDescriptionTitle,
      GtmSource,
      localePath,
    }
  },
  data() {
    return {
      showSimilarCategory: false, // TODO: 暫時關閉, 等待搜尋頁再開啟
      showContentMenu: false,
      showRecommendedProducts: false,
      recommendedProducts: [],
      isMdAndUp: false,
      isMdAndDown: false,
      isMobile: false,
      activatedMenu: 'productInfo',
      userPosition: { lat: '', lng: '' },
    }
  },
  computed: {
    loggedInUser() {
      return this.$auth.user
    },
    showRecommendSheet() {
      return (this.showSimilarCategory && this.product.tags.length > 0) || this.otherProducts.length > 0 || this.showRecommendedProducts
    },
    isMainProductInfoShown() {
      return !!this.product.description
    },
    contentMenu() {
      let res = []
      if (this.isMainProductInfoShown) {
        res = res.concat({
          text: this.productDetailTitle,
          value: 'productInfo',
          targetID: 'main-product-info',
          poissonID: 'main-product-info',
        })
      }
      if (this.product?.productIntro?.length > 0) {
        res = res.concat({
          text: this.productDescriptionTitle,
          value: 'productIntro',
          targetID: 'main-product-intro',
          poissonID: 'main-product-intro',
        })
      }
      if (this.product?.price_list?.length > 0) {
        res = res.concat({
          text: this.$t('price_list_title'),
          value: 'productPriceList',
          targetID: 'main-product-price-list',
          poissonID: 'main-product-price-list',
        })
      }
      if (this.product?.productShoppingNotices?.length > 0) {
        res = res.concat({
          text: this.$t('purchasing_notice_title'),
          value: 'productShoppingNotice',
          targetID: 'main-product-shopping-notice',
          poissonID: 'main-product-shopping-notice',
        })
      }

      return [
        ...res,
        // { text: this.$t('all_comment'), value: 'productComments', targetID: 'main-product-comments', poissonID: 'main-product-comments' },
        {
          text: this.$t('branch_info'),
          value: 'branchIntro',
          targetID: 'main-branch-intro',
          poissonID: 'main-branch-intro',
        },
        {
          text: this.$t('all_address'),
          value: 'branchPosition',
          targetID: 'main-branch-position',
          poissonID: 'main-branch-position',
        },
        {
          text: this.$t('all_notice', { branchname: '' }),
          value: 'productNotices',
          targetID: 'main-product-notices',
          poissonID: 'main-product-notices',
        },
      ]
    },
    breadcrumbs() {
      const breadcrumbs = []
      if (this.branch) {
        breadcrumbs.push({
          text: this.product.branch_name,
          to: this.localePath({ name: 'branches-id', params: { id: this.product.bid } }),
        })
      }
      if (this.branch && this.product) {
        breadcrumbs.push({ text: this.product.product_name })
      }
      return breadcrumbs
    },
    onboard() {
      return this.product.onboard === 1
    },
    branchComments() {
      return { rating: this.branch.rating, totalCount: this.branch.number_of_comments }
    },
    bookingProductCardStyle() {
      return {
        // 88px = 64px(header height) + 24px(margin-top)，and one notification height is 46px
        top: `${this.notifications.length * 46 + 88}px`,
      }
    },
    ...mapGetters({
      user: 'authentication/getUser',
      otherProducts: 'product/otherProducts',
      comments: 'comments/productComments',
      allRegions: 'region/allRegions',
      region: 'region/region',
      branch: 'branch/branch',
      notifications: 'notification/notifications',
    }),
    ...mapState(usePlatformStore, ['isFunBookV2DomainCustomization']),
  },
  mounted() {
    this.getLocation()
    this.initContentMenu()
    this.onResize()
    this.sendTrackingCode()
    window.addEventListener('scroll', this.handleScroll, { passive: true })

    if (this.user) {
      this.getRegionRecommendedProducts()
    }

    this.setNotification()
  },
  beforeUnmount() {
    this.$store.dispatch('notification/resetNotification')
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    getLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => {
          this.userPosition.lat = position.coords.latitude
          this.userPosition.lng = position.coords.longitude
          this.getProductInfo()
        })
      }
    },
    onResize() {
      this.isMdAndUp = window.innerWidth > 1120
      this.isMdAndDown = window.innerWidth <= 1120
      this.isMobile = window.innerWidth < 600
    },
    getProductInfo() {
      const { lat, lng } = this.userPosition
      const queryForProductInfo = { lat: lat, lng: lng }

      this.$store.dispatch('product/getProductInfo', {
        pid: this.product.pid,
        params: queryForProductInfo,
        isCdnApiRequestedByPath: this.isFunBookV2DomainCustomization,
      })
    },
    initContentMenu() {
      this.setShowContentMenu()
      if (this.showContentMenu) {
        this.contentMenuScrollEvent()
      }
    },
    setShowContentMenu() {
      this.showContentMenu = this.$el.getBoundingClientRect().top + 509 < 0
    },
    contentMenuScrollEvent(event) {
      const pageBounding = this.$el.getBoundingClientRect()
      const isPageBottom = pageBounding.bottom < document.documentElement.clientHeight
      if (isPageBottom) {
        this.activatedMenu = this.contentMenu[this.contentMenu.length - 1].value
      } else {
        let minRangeTarget = {}
        this.setShowContentMenu()
        this.contentMenu.forEach(menu => {
          if (document.getElementById(menu.poissonID)) {
            const elBounding = document.getElementById(menu.poissonID).getBoundingClientRect()

            // 128: header高度 + 導覽列高度
            const isDisappear = elBounding.height + elBounding.top - 128 < 0
            if (!isDisappear && elBounding.top < 150) {
              minRangeTarget = Object.assign({}, menu)
            }
          }
        })
        this.activatedMenu = minRangeTarget.value
      }
    },
    getRegionRecommendedProducts() {
      const regionID = this.product.region_id
      this.$store
        .dispatch('region/getRegionRecommendedProducts', regionID)
        .then(res => {
          const products = res.data
          this.showRecommendedProducts = res.total_count > 0
          this.recommendedProducts = products?.filter(
            product => product.sales_mode !== ProductSalesMode.WineDelivery && product.sales_mode !== ProductSalesMode.Movie
          )
        })
        .catch(err => {
          console.error(err)
        })
    },
    handleScroll(event) {
      this.contentMenuScrollEvent()
    },
    openCommentDialog() {
      this.$refs.productCommentSheet.openCommentModal()
    },
    goToBranch() {
      this.$router.push(this.localePath(`/funbook/${this.product.bid}/branch`))
    },
    goToThemesPage(tag) {
      console.log(tag)
    },
    setNotification() {
      const notificationSetting = []
      if (this.product.flash_time_type && this.product.flash_time_type !== 'hidden') {
        notificationSetting.push(this.getFlashNotification({ flashTime: this.product.flash_time, timeType: this.product.flash_time_type }))
      }

      // if (this.product.delivery) {
      //   notificationSetting.push(this.getDeliveryNotification({ freeAmount: this.product.delivery.delivery_free_amount }))
      // }

      this.$store.dispatch('notification/setNotifications', notificationSetting)
    },
    sendTrackingCode() {
      this.$gtm.push({ ecommerce: null })
      this.$gtm.push({
        event: 'view_item',
        value: this.product.discount,
        items: [
          {
            id: this.product.pid,
            google_business_vertical: GOOGLE_BUSINESS_VERTICAL,
          },
        ],
        ecommerce: {
          currencyCode: this.product.symbol_code,
          detail: {
            products: [
              {
                name: this.product.product_name,
                id: this.product.pid,
                price: this.product.discount,
                brand: this.product.branch_name,
                category: this.product.category_id,
                platform: 'FunBook',
                payment_mode: this.product.is_paid_onsite ? PaymentMode.ONSITE : PaymentMode.ONLINE,
              },
            ],
          },
        },
      })
    },
  },
})
</script>

<style lang="scss" scoped>
.product-info-container {
  margin-top: 24px;

  @include xxs {
    margin-top: 0px;
  }
}
</style>

<style lang="scss"></style>
